import type { PageEndpoint } from 'features/knowledgeDocumentViewer/components/revisionCompare/revisionMemberCanonical'
import {
  type FimRevisionDiffDetailData,
  type FimRevisionDiffDetailEntry,
  REVISION_OBJECT_STATUS_SCHEMA,
  type RevisionDiffDetailBox,
  type RevisionDiffDetailEvent,
  type RevisionDiffDetailsFile,
  type RevisionDiffDetailShape,
  type RevisionObjectStatus,
} from 'features/knowledgeDocumentViewer/hooks/useRevisionDiffDetails'
import type { NormalizedBBox } from 'features/knowledgeDocumentViewer/types/ocrViewer'
import { unionNormalizedBBoxes } from 'features/knowledgeDocumentViewer/utils/bboxHelpers'
import type { PageRevisionFamilyMember } from 'features/knowledgeDocumentViewer/utils/pageRevisionFamily'

export const REVISION_OBJECT_STATUSES: readonly RevisionObjectStatus[] =
  REVISION_OBJECT_STATUS_SCHEMA.options

/** Bucket key for objects and events with no producer-supplied impact. */
export const UNCLASSIFIED_IMPACT_KEY = ''

const EMPTY_IMPACTS: ReadonlySet<string> = new Set()

export function impactKey(impact: string | null): string {
  const trimmed = impact?.trim()
  return trimmed ? trimmed : UNCLASSIFIED_IMPACT_KEY
}

export type RevisionOverlayPrimitive =
  | { kind: 'rect'; x0: number; y0: number; x1: number; y1: number }
  | { kind: 'line'; points: readonly (readonly [number, number])[] }

export type RevisionObjectTooltip = {
  status: RevisionObjectStatus
  title: string | null
  cls: string | null
  headline: string | null
  details: string | null
  oldText: string | null
  newText: string | null
  impact: string | null
}

export type RevisionOverlayShape = {
  id: string
  status: RevisionObjectStatus
  objectId: string
  primitives: RevisionOverlayPrimitive[]
  tooltip: RevisionObjectTooltip
}

export type RevisionDiffSide = {
  shapes: RevisionOverlayShape[]
}

const pageKey = (bqDocumentId: string, pageNum: number) =>
  `${bqDocumentId}\u0000${pageNum}`

/** Maps a raw (document, page) to its revision's canonical endpoint. */
export type CanonicalEndpoint = (
  bqDocumentId: string,
  pageNum: number
) => PageEndpoint

type DiffEndpoints = Pick<
  FimRevisionDiffDetailData,
  'from_document_id' | 'from_page_num' | 'to_document_id' | 'to_page_num'
>

const detailFrom = (
  data: DiffEndpoints,
  canonicalEndpoint: CanonicalEndpoint
) => canonicalEndpoint(data.from_document_id, data.from_page_num)

const detailTo = (data: DiffEndpoints, canonicalEndpoint: CanonicalEndpoint) =>
  canonicalEndpoint(data.to_document_id, data.to_page_num)

/**
 * Compares canonical endpoints on both sides, so a detail stored against a
 * re-upload sibling still matches its revision's canonical member.
 */
export function diffTouchesMember(
  data: DiffEndpoints,
  bqDocumentId: string,
  pageNum: number,
  canonicalEndpoint: CanonicalEndpoint
): boolean {
  const member = canonicalEndpoint(bqDocumentId, pageNum)
  const memberKey = pageKey(member.bqDocumentId, member.pageNum)
  const from = detailFrom(data, canonicalEndpoint)
  const to = detailTo(data, canonicalEndpoint)
  return (
    pageKey(from.bqDocumentId, from.pageNum) === memberKey ||
    pageKey(to.bqDocumentId, to.pageNum) === memberKey
  )
}

/**
 * A re-publish can leave several details for one pair, so we keep the newest
 * by `generated_at` (ISO-8601, so lexicographic order is chronological),
 * preferring any timestamped entry over an untimestamped one and falling back
 * to the first when none carry a timestamp.
 */
export function newestDetail(
  candidates: readonly FimRevisionDiffDetailEntry[]
): FimRevisionDiffDetailEntry | undefined {
  if (candidates.length <= 1) return candidates[0]
  return candidates.reduce((best, candidate) => {
    const bestTs = best.data.generated_at
    const candidateTs = candidate.data.generated_at
    if (candidateTs === null || candidateTs === undefined) return best
    if (bestTs === null || bestTs === undefined) return candidate
    return candidateTs > bestTs ? candidate : best
  })
}

/** Directional lookup of the newest detail for one from/to canonical page pair. */
export function resolvePairDetail(
  details: readonly FimRevisionDiffDetailEntry[],
  from: PageEndpoint,
  to: PageEndpoint,
  canonicalEndpoint: CanonicalEndpoint
): FimRevisionDiffDetailEntry | undefined {
  const fromKey = canonicalEndpoint(from.bqDocumentId, from.pageNum)
  const toKey = canonicalEndpoint(to.bqDocumentId, to.pageNum)
  return newestDetail(
    details.filter((detail) => {
      const detailFromEndpoint = detailFrom(detail.data, canonicalEndpoint)
      const detailToEndpoint = detailTo(detail.data, canonicalEndpoint)
      return (
        pageKey(detailFromEndpoint.bqDocumentId, detailFromEndpoint.pageNum) ===
          pageKey(fromKey.bqDocumentId, fromKey.pageNum) &&
        pageKey(detailToEndpoint.bqDocumentId, detailToEndpoint.pageNum) ===
          pageKey(toKey.bqDocumentId, toKey.pageNum)
      )
    })
  )
}

/** Keeps details whose from and to pages are both among the given members. */
export function filterDetailsToMembers(
  details: readonly FimRevisionDiffDetailEntry[],
  members: readonly PageEndpoint[],
  canonicalEndpoint: CanonicalEndpoint
): FimRevisionDiffDetailEntry[] {
  const memberKeys = new Set(
    members.map((member) => {
      const endpoint = canonicalEndpoint(member.bqDocumentId, member.pageNum)
      return pageKey(endpoint.bqDocumentId, endpoint.pageNum)
    })
  )
  return details.filter((detail) => {
    const from = detailFrom(detail.data, canonicalEndpoint)
    const to = detailTo(detail.data, canonicalEndpoint)
    return (
      memberKeys.has(pageKey(from.bqDocumentId, from.pageNum)) &&
      memberKeys.has(pageKey(to.bqDocumentId, to.pageNum))
    )
  })
}

/**
 * Picks the default detail to show: the one between the open page and the
 * family member immediately before it (members are ordered latest-first).
 * Falls back to a detail that still touches the open page/document (not just
 * `details[0]`, which may belong to a different sheet of a multi-page
 * document) when no exact adjacent-pair detail exists. When several details
 * match, the newest by `generated_at` wins.
 */
export function selectDefaultDetail(
  details: readonly FimRevisionDiffDetailEntry[],
  members: readonly PageRevisionFamilyMember[],
  currentBqDocumentId: string,
  currentPageNum: number,
  canonicalEndpoint: CanonicalEndpoint
): string | undefined {
  const currentIndex = members.findIndex(
    (member) =>
      member.bqDocumentId === currentBqDocumentId &&
      member.pageNum === currentPageNum
  )
  const priorMember = currentIndex >= 0 ? members[currentIndex + 1] : undefined

  if (priorMember) {
    const adjacent = newestDetail(
      details.filter(
        (detail) =>
          diffTouchesMember(
            detail.data,
            currentBqDocumentId,
            currentPageNum,
            canonicalEndpoint
          ) &&
          diffTouchesMember(
            detail.data,
            priorMember.bqDocumentId,
            priorMember.pageNum,
            canonicalEndpoint
          )
      )
    )
    if (adjacent) return adjacent.entryId
  }

  return newestDetail(
    details.filter((detail) =>
      diffTouchesMember(
        detail.data,
        currentBqDocumentId,
        currentPageNum,
        canonicalEndpoint
      )
    )
  )?.entryId
}

export type RevisionDetailEventRow = {
  id: string
  headline: string
  details: string
  status: RevisionObjectStatus
  kind: string | null
  impact: string | null
  category: string | null
  action: string | null
  tier: string | null
  confidence: string | null
  oldText: string | null
  newText: string | null
  pixelEvidence: Record<string, unknown> | null
  cloudState: string | null
  fromBox: RevisionDiffDetailBox | null
  toBox: RevisionDiffDetailBox | null
  objectRefs: string[]
}

export type RevisionDetailSection = {
  section: string
  text: string
  where: string | null
  events: RevisionDetailEventRow[]
}

export type RevisionDiffDetailViewModel = {
  from: RevisionDiffSide
  to: RevisionDiffSide
  sections: RevisionDetailSection[]
  /** Events no `view` section references, so every event stays reachable. */
  unsectionedEvents: RevisionDetailEventRow[]
  countsByStatus: Record<RevisionObjectStatus, number>
  /** Drawn-box counts keyed by `impactKey`, in first-seen order. */
  countsByImpact: ReadonlyMap<string, number>
  verdict: string | null
  boxesByObjectId: Map<
    string,
    {
      fromBox: RevisionDiffDetailBox | null
      toBox: RevisionDiffDetailBox | null
    }
  >
  eventIdByObjectId: Map<string, string>
}

type DetailObject = RevisionDiffDetailsFile['objects'][number]

function toNormalizedBBox(box: RevisionDiffDetailBox): NormalizedBBox {
  return {
    left: box.x0,
    top: box.y0,
    width: box.x1 - box.x0,
    height: box.y1 - box.y0,
  }
}

function fromNormalizedBBox(box: NormalizedBBox): RevisionDiffDetailBox {
  return {
    x0: box.left,
    y0: box.top,
    x1: box.left + box.width,
    y1: box.top + box.height,
  }
}

/** Bounding box over every rect corner and polyline point of `primitives`. */
function boundsOfPrimitives(
  primitives: RevisionOverlayPrimitive[]
): RevisionDiffDetailBox | null {
  const xs: number[] = []
  const ys: number[] = []
  for (const primitive of primitives) {
    if (primitive.kind === 'rect') {
      xs.push(primitive.x0, primitive.x1)
      ys.push(primitive.y0, primitive.y1)
    } else {
      for (const [x, y] of primitive.points) {
        xs.push(x)
        ys.push(y)
      }
    }
  }
  if (xs.length === 0) return null
  return {
    x0: Math.min(...xs),
    y0: Math.min(...ys),
    x1: Math.max(...xs),
    y1: Math.max(...ys),
  }
}

/** Bounds of exactly the validated primitives drawn for `side`. */
export function objectSideBounds(
  object: DetailObject,
  side: 'from' | 'to'
): RevisionDiffDetailBox | null {
  return boundsOfPrimitives(objectPrimitives(object, side))
}

function unionSideBounds(
  objects: readonly DetailObject[],
  side: 'from' | 'to'
): RevisionDiffDetailBox | null {
  const boxes = objects.flatMap((object) => {
    const bounds = objectSideBounds(object, side)
    return bounds ? [toNormalizedBBox(bounds)] : []
  })
  if (boxes.length === 0) return null
  return fromNormalizedBBox(unionNormalizedBBoxes(boxes))
}

function eventStatus(
  fromBox: RevisionDiffDetailBox | null,
  toBox: RevisionDiffDetailBox | null,
  refStatuses: readonly RevisionObjectStatus[]
): RevisionObjectStatus {
  if (refStatuses.length > 0) return refStatuses[0]
  if (toBox && !fromBox) return 'added'
  if (fromBox && !toBox) return 'removed'
  return 'changed'
}

function buildEventRow(
  event: RevisionDiffDetailEvent,
  objectsById: ReadonlyMap<string, RevisionDiffDetailsFile['objects'][number]>
): RevisionDetailEventRow {
  const objectRefs = event.object_refs ?? []
  const refs = objectRefs.flatMap((ref) => objectsById.get(ref) ?? [])
  const fromBox = event.from_box ?? unionSideBounds(refs, 'from')
  const toBox = event.to_box ?? unionSideBounds(refs, 'to')
  return {
    id: event.id,
    headline: event.headline,
    details: event.details,
    status: eventStatus(
      fromBox,
      toBox,
      refs.map((object) => object.status)
    ),
    kind: event.kind ?? null,
    impact: event.impact ?? null,
    category: event.category ?? null,
    action: event.action ?? null,
    tier: event.tier ?? null,
    confidence: event.confidence ?? null,
    oldText: event.old_text ?? null,
    newText: event.new_text ?? null,
    pixelEvidence: event.pixel_evidence ?? null,
    cloudState: event.cloud_state ?? null,
    fromBox,
    toBox,
    objectRefs,
  }
}

const BOX_COORD_MIN = -0.1
const BOX_COORD_MAX = 1.1

function isInRange(coord: number): boolean {
  return (
    Number.isFinite(coord) && coord >= BOX_COORD_MIN && coord <= BOX_COORD_MAX
  )
}

function isDrawablePrimitive(primitive: RevisionOverlayPrimitive): boolean {
  if (primitive.kind === 'rect') {
    const { x0, y0, x1, y1 } = primitive
    if (![x0, y0, x1, y1].every(isInRange)) return false
    return x1 > x0 && y1 > y0
  }
  return (
    primitive.points.length >= 2 &&
    primitive.points.every(([x, y]) => isInRange(x) && isInRange(y))
  )
}

function shapeToPrimitive(
  shape: RevisionDiffDetailShape
): RevisionOverlayPrimitive {
  if (shape.t === 'rect') {
    const [x0, y0, x1, y1] = shape.p
    return { kind: 'rect', x0, y0, x1, y1 }
  }
  return { kind: 'line', points: shape.p }
}

function objectPrimitives(
  object: RevisionDiffDetailsFile['objects'][number],
  side: 'from' | 'to'
): RevisionOverlayPrimitive[] {
  const shapes = side === 'from' ? object.from_shapes : object.to_shapes
  if (shapes && shapes.length > 0) {
    return shapes.map(shapeToPrimitive).filter(isDrawablePrimitive)
  }
  const box = side === 'from' ? object.from_box : object.to_box
  if (!box) return []
  const primitive: RevisionOverlayPrimitive = {
    kind: 'rect',
    x0: box.x0,
    y0: box.y0,
    x1: box.x1,
    y1: box.y1,
  }
  return isDrawablePrimitive(primitive) ? [primitive] : []
}

function detailSideShapes(
  objects: RevisionDiffDetailsFile['objects'],
  side: 'from' | 'to',
  hiddenStatuses: ReadonlySet<RevisionObjectStatus>,
  hiddenImpacts: ReadonlySet<string>,
  tooltipByObjectId: ReadonlyMap<string, RevisionObjectTooltip>
): RevisionOverlayShape[] {
  const result: RevisionOverlayShape[] = []
  for (const object of objects) {
    if (hiddenStatuses.has(object.status)) continue
    const tooltip = tooltipByObjectId.get(object.id)
    if (hiddenImpacts.has(impactKey(tooltip?.impact ?? null))) continue
    const primitives = objectPrimitives(object, side)
    if (primitives.length === 0) continue
    result.push({
      id: `${side}-${object.id}`,
      status: object.status,
      objectId: object.id,
      primitives,
      tooltip: tooltip ?? {
        status: object.status,
        title: object.title ?? null,
        cls: object.cls ?? null,
        headline: null,
        details: null,
        oldText: null,
        newText: null,
        impact: null,
      },
    })
  }
  return result
}

function countBoxesByStatus(
  objects: RevisionDiffDetailsFile['objects']
): Record<RevisionObjectStatus, number> {
  const counts = { added: 0, removed: 0, changed: 0, matched: 0 }
  for (const object of objects) {
    counts[object.status] += (object.from_box ? 1 : 0) + (object.to_box ? 1 : 0)
  }
  return counts
}

function countBoxesByImpact(
  objects: RevisionDiffDetailsFile['objects'],
  tooltipByObjectId: ReadonlyMap<string, RevisionObjectTooltip>
): Map<string, number> {
  const counts = new Map<string, number>()
  for (const object of objects) {
    const boxes = (object.from_box ? 1 : 0) + (object.to_box ? 1 : 0)
    if (boxes === 0) continue
    const key = impactKey(tooltipByObjectId.get(object.id)?.impact ?? null)
    counts.set(key, (counts.get(key) ?? 0) + boxes)
  }
  return counts
}

/**
 * Maps a detail entry (+ its lazily loaded `details.json`) into a view model.
 * Hidden statuses and hidden impacts filter overlay boxes and events alike. Dangling
 * `object_refs` / `event_ids` are skipped rather than failing. Kept pure so it
 * can be unit tested without any Rolodex/React dependency.
 */
export function buildRevisionDiffDetailViewModel(
  detailData: FimRevisionDiffDetailData,
  detailsFile: RevisionDiffDetailsFile,
  hiddenStatuses: ReadonlySet<RevisionObjectStatus>,
  hiddenImpacts: ReadonlySet<string> = EMPTY_IMPACTS
): RevisionDiffDetailViewModel {
  const objectsById = new Map(
    detailsFile.objects.map((object) => [object.id, object])
  )
  const rowsById = new Map(
    detailsFile.events.map((event) => [
      event.id,
      buildEventRow(event, objectsById),
    ])
  )
  const eventByObjectId = new Map<string, RevisionDetailEventRow>()
  const eventIdByObjectId = new Map<string, string>()
  for (const row of rowsById.values()) {
    for (const objectRef of row.objectRefs) {
      if (eventByObjectId.has(objectRef)) continue
      eventByObjectId.set(objectRef, row)
      eventIdByObjectId.set(objectRef, row.id)
    }
  }
  const tooltipByObjectId = new Map<string, RevisionObjectTooltip>()
  const boxesByObjectId: RevisionDiffDetailViewModel['boxesByObjectId'] =
    new Map()
  for (const object of detailsFile.objects) {
    const ev = eventByObjectId.get(object.id)
    tooltipByObjectId.set(object.id, {
      status: object.status,
      title: object.title ?? null,
      cls: object.cls ?? null,
      headline: ev?.headline ?? null,
      details: ev?.details ?? null,
      oldText: ev?.oldText ?? null,
      newText: ev?.newText ?? null,
      impact: ev?.impact ?? null,
    })
    boxesByObjectId.set(object.id, {
      fromBox: objectSideBounds(object, 'from'),
      toBox: objectSideBounds(object, 'to'),
    })
  }
  const isVisible = (row: RevisionDetailEventRow) =>
    !hiddenStatuses.has(row.status) && !hiddenImpacts.has(impactKey(row.impact))

  const sectionedEventIds = new Set<string>()
  const sections: RevisionDetailSection[] = detailsFile.view.map((section) => {
    const events = (section.event_ids ?? []).flatMap((eventId) => {
      sectionedEventIds.add(eventId)
      const row = rowsById.get(eventId)
      return row && isVisible(row) ? [row] : []
    })
    return {
      section: section.section,
      text: section.text,
      where: section.where ?? null,
      events,
    }
  })

  const unsectionedEvents = Array.from(rowsById.values()).filter(
    (row) => !sectionedEventIds.has(row.id) && isVisible(row)
  )

  return {
    from: {
      shapes: detailSideShapes(
        detailsFile.objects,
        'from',
        hiddenStatuses,
        hiddenImpacts,
        tooltipByObjectId
      ),
    },
    to: {
      shapes: detailSideShapes(
        detailsFile.objects,
        'to',
        hiddenStatuses,
        hiddenImpacts,
        tooltipByObjectId
      ),
    },
    sections,
    unsectionedEvents,
    countsByStatus: countBoxesByStatus(detailsFile.objects),
    countsByImpact: countBoxesByImpact(detailsFile.objects, tooltipByObjectId),
    verdict: detailData.verdict ?? null,
    boxesByObjectId,
    eventIdByObjectId,
  }
}
